import React, {
    createContext,
    useContext,
    useState,
    useMemo,
    ReactNode,
    useEffect,
} from 'react';
import { CallCenterLocation, CorporationConfiguration } from '../util/Corporations';
import { fetchLocations, fetchCorpConfig, USER_UUID_STORAGE_KEY } from '../api/config';
import { getLocationCode } from '../util/Application';
import { useCookies } from 'react-cookie';

interface CallCenterContextValue {
    configuration: CorporationConfiguration | null;
    locations: CallCenterLocation[];
    /** Loading/error for the corp's location list itself (mtas + RapidStor) —
     * a hard failure here means the corp can't be bootstrapped at all. */
    locationsLoading: boolean;
    locationsError: string | null;
    /** Loading/error for one specific location's corp config (automate) —
     * only kicks in once a location is chosen (from the URL or the picker).
     * A `configError` with no exception is a recoverable "this location isn't
     * set up yet" state, not a hard failure. */
    configLoading: boolean;
    configError: string | null;
    /** The location actually resolved (from the URL or a data-attribute) —
     * use this for the location dropdown's value, not the URL directly. */
    selectedLocationCode: string | null;
    sortedLocations: CallCenterLocation[];
    agentName: string;
    agentComments: string;
    /** What's actually saved (i.e. what the JBL widget will read from the
     * cookie) — distinct from `agentName`/`agentComments`, which track the
     * live, possibly-unsaved input values. */
    savedAgentName: string;
    savedAgentComments: string;
    setAgentDetails: (overrides?: { agentName?: string; agentComments?: string }) => void;
    clearAgentDetails: () => void;
    startNewEnquiry: () => void;
    setAgentName: (name: string) => void;
    setAgentComments: (comments: string) => void;
}

// Set by the JBL widget for the current tenant's booking session.
const TENANT_SESSION_COOKIES = [
    'contactData',
    'reservation',
    'persona',
    'sessionId',
    'selectedCustomerType',
];
const SESSION_STORAGE_KEYS = ['rapidstor-user-uuid', USER_UUID_STORAGE_KEY];

const CallCenterContext = createContext<CallCenterContextValue | undefined>(undefined);

export const CallCenterProvider: React.FC<{ corpCode: string; children: ReactNode }> = ({
    corpCode,
    children,
}) => {
    /** Hooks */
    const [agentDetailCookies, setAgentDetailCookies] = useCookies(['agentDetails']);
    const [, , removeContactDataCookies] = useCookies(['contactData']);
    const [, , removeReservationCookies] = useCookies(['reservation']);
    const [, , removeSessionCookie] = useCookies(TENANT_SESSION_COOKIES);

    /** State */
    const [locations, setLocations] = useState<CallCenterLocation[]>([]);
    const [locationsLoading, setLocationsLoading] = useState<boolean>(true);
    const [locationsError, setLocationsError] = useState<string | null>(null);

    const [configuration, setConfiguration] = useState<CorporationConfiguration | null>(
        null
    );
    const [configLoading, setConfigLoading] = useState<boolean>(false);
    const [configError, setConfigError] = useState<string | null>(null);
    // The location actually resolved (from ?locationCode= or data-LocationCode),
    // independent of the URL — TopBar's dropdown reflects this rather than
    // re-deriving it from useSearchParams(), which misses the DOM-attribute case.
    const [selectedLocationCode, setSelectedLocationCode] = useState<string | null>(null);

    const [agentName, setAgentName] = useState<string>('');
    const [agentComments, setAgentComments] = useState<string>('');

    const savedAgentName = agentDetailCookies?.agentDetails?.agentName ?? '';
    const savedAgentComments = agentDetailCookies?.agentDetails?.agentComments ?? '';

    /**
     * Phase 1: the corp's location list (mtas + RapidStor) — fast, and never
     * needs automate, so it can render a location picker before any
     * corp-config call happens at all.
     */
    useEffect(() => {
        let cancelled = false;

        setLocationsLoading(true);
        setLocationsError(null);

        fetchLocations(corpCode)
            .then((fetchedLocations) => {
                if (cancelled) return;
                if (fetchedLocations.length === 0) {
                    setLocationsError(`No locations found for corp "${corpCode}"`);
                    return;
                }
                setLocations(fetchedLocations);
            })
            .catch((err: Error) => {
                console.error('Failed to load locations', err);
                if (!cancelled) {
                    setLocationsError(err.message);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLocationsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [corpCode]);

    /**
     * Phase 2: once locations are known, fetch corp config for whichever
     * location is already in the URL. Every location switch (including the
     * very first pick, from the location-picker screen) is a full page
     * reload — this provider remounts from scratch, so there's no separate
     * "user just picked one" code path to keep in sync with this.
     *
     * Deliberately does NOT fall back to trying other locations if this one
     * has no config — some automate fields (e.g. a MultiSiteLink
     * `unitTypeRegex`) are genuinely site-specific, so silently substituting
     * a different location's config would serve wrong data under the
     * location the agent actually selected.
     */
    useEffect(() => {
        if (locations.length === 0) {
            return;
        }

        const preferredLocationCode = getLocationCode();
        const selected = preferredLocationCode
            ? locations.find((location) => location.code === preferredLocationCode)
            : null;

        setSelectedLocationCode(selected?.code ?? null);

        if (!selected) {
            // No (valid) location chosen yet — the location-picker screen
            // handles this; nothing to fetch.
            setConfiguration(null);
            setConfigError(null);
            return;
        }

        let cancelled = false;

        setConfigLoading(true);
        setConfigError(null);

        fetchCorpConfig(corpCode, selected.code)
            .then((config) => {
                if (cancelled) return;
                if (!config) {
                    setConfigError(
                        `"${selected.name}" isn't set up for the call center tool yet — please choose a different location.`
                    );
                    return;
                }
                setConfiguration(config);
            })
            .catch((err: Error) => {
                console.error('Failed to load call center configuration', err);
                if (!cancelled) {
                    setConfigError(err.message);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setConfigLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [corpCode, locations]);

    /**
     * Sorted list of locations by name
     *
     * @returns {CallCenterLocation[]} sortedLocations
     */
    const sortedLocations = useMemo((): CallCenterLocation[] => {
        return [...locations].sort((a, b) => a.name.localeCompare(b.name));
    }, [locations]);

    /**
     * Set agent details in cookies. `overrides` lets a caller pass values
     * that haven't landed in `agentName`/`agentComments` state yet (React
     * batches `setAgentName`/`setAgentComments` — reading the state here
     * right after calling them would still see the previous render's values).
     *
     * @returns {void}
     */
    const setAgentDetails = (overrides?: {
        agentName?: string;
        agentComments?: string;
    }): void => {
        setAgentDetailCookies('agentDetails', {
            agentName: overrides?.agentName ?? agentName,
            agentComments: overrides?.agentComments ?? agentComments,
        });
    };

    /**
     * Clear agent details from cookies
     *
     * @returns {void}
     */
    const clearAgentDetails = (): void => {
        setAgentDetailCookies('agentDetails', {
            agentName: '',
            agentComments: '',
        });
        setAgentName('');
        setAgentComments('');
        removeContactDataCookies('contactData', { path: `${configuration?.baseUrl}` });
        removeReservationCookies('reservation', { path: `${configuration?.baseUrl}` });
    };

    /**
     * Clear the widget's tenant session so the next enquiry starts fresh.
     * The widget sets these cookies at '/' or at the page path, so remove
     * them at every path it could use. Agent details are kept.
     *
     * @returns {void}
     */
    const startNewEnquiry = (): void => {
        const paths = new Set(['/', window.location.pathname]);
        if (configuration?.baseUrl) {
            paths.add(configuration.baseUrl);
        }

        TENANT_SESSION_COOKIES.forEach((name) =>
            paths.forEach((path) => removeSessionCookie(name, { path }))
        );

        try {
            SESSION_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
        } catch {
            // Storage unavailable — nothing to clear.
        }

        // The widget keeps its own in-memory state, so reload to restart it.
        window.location.reload();
    };

    /**
     * Preload the agent details from cookies on page load
     */
    useEffect(() => {
        if (agentDetailCookies && agentDetailCookies.agentDetails) {
            setAgentName(agentDetailCookies.agentDetails.agentName);
            setAgentComments(agentDetailCookies.agentDetails.agentComments);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <CallCenterContext.Provider
            value={{
                configuration,
                locations,
                locationsLoading,
                locationsError,
                configLoading,
                configError,
                selectedLocationCode,
                sortedLocations,
                agentName,
                agentComments,
                savedAgentName,
                savedAgentComments,
                setAgentDetails,
                clearAgentDetails,
                startNewEnquiry,
                setAgentName,
                setAgentComments,
            }}
        >
            {children}
        </CallCenterContext.Provider>
    );
};

export const useCallCenter = (): CallCenterContextValue => {
    const context = useContext(CallCenterContext);
    if (!context) {
        throw new Error('useCallCenter must be used within a CallCenterProvider');
    }
    return context;
};
